import {
  Badge,
  Icon,
  Text,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  Stack,
  SkeletonText,
  Tooltip,
  PanelActions,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useEnvHealthApiTriggeredBroadcasts, useWorkspaceHealthSimulation } from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useActiveHealthTab } from '../hooks';

export function ApiTriggeredBroadcastsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { activeTab } = useActiveHealthTab();
  const { simulate } = useWorkspaceHealthSimulation();

  const broadcastsQuery = useEnvHealthApiTriggeredBroadcasts(workspaceId, simulate, activeTab === 'messaging');

  const data = broadcastsQuery.data?.body;
  const pendingCount = data?.pending_count ?? 0;
  const withErrorsCount = data?.with_errors_count ?? 0;
  const processedToday = data?.processed_today ?? 0;
  const recipientsTodayTotal = data?.recipients_today_total ?? 0;

  return (
    <Panel style={{ height: '100%' }}>
      <PanelHeader>
        <PanelTitle>API-triggered broadcasts</PanelTitle>
        <PanelActions>
          <Link
            href={`/workspaces/${workspaceId}/journeys/broadcasts/api-triggered`}
            variant="primary"
            onClick={() => void track('workspaceHealth', 'viewAllClicked', { Destination: 'api_triggered_broadcasts' })}
          >
            View all
          </Link>
        </PanelActions>
      </PanelHeader>
      <PanelSection>
        {broadcastsQuery.isLoading ? (
          <SkeletonText size="s" lines={5} />
        ) : (
          <Stack gap="100">
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <Text size="s">Pending</Text>
              <Badge>{pendingCount.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <Text size="s">With errors</Text>
              <Badge>{withErrorsCount.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s">Processed today</Text>
                <Tooltip content="API-triggered broadcast messages processed since midnight UTC.">
                  <Icon name="help" size="xs" color="subtle" />
                </Tooltip>
              </InlineStack>
              <Badge>{processedToday.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s">Recipients today</Text>
                <Tooltip content="Total recipients for API-triggered broadcasts since midnight UTC.">
                  <Icon name="help" size="xs" color="subtle" />
                </Tooltip>
              </InlineStack>
              <Badge>{recipientsTodayTotal.toLocaleString()}</Badge>
            </InlineStack>
          </Stack>
        )}
      </PanelSection>
    </Panel>
  );
}
